import React, { useCallback, useEffect, useState } from 'react';
import { ForgeButton, ForgeIcon, ForgeScaffold, ForgeTab, ForgeTabBar } from '@tylertech/forge-react';
import _ from 'lodash';
import AlertWrapper from './components/alert_wrapper';
import type { AssetBrowserProps } from './types';
import { I18n } from 'common/i18n';

import styles from './ForgeAssetBrowserWrapper.module.scss';
import { useDispatch, useSelector } from 'react-redux';
import type { ThunkDispatch } from 'redux-thunk';
import type { AnyAction } from 'redux';
import getQueryParameterByName from 'common/js_utils/getQueryParameterByName';
import { updatePageSize } from './actions/page_size';
import { setInitialTab, changeTab } from './actions/header';
import { setAssetBrowserProps } from './actions/asset_browser_props';
import { getTabDisplayText } from './components/header';
import { selectActiveTab } from './selectors/header';
import { TAB_MY_ASSETS } from './lib/constants';
import { fetchInitialResults } from './lib/helpers/cetera';
import { AssetBrowserSearch } from './asset-browser-search';

// Minimal state interface for AssetBrowser Redux store
interface AssetBrowserState {
  catalog: {
    initialResultsFetched: boolean;
    results: unknown[];
  };
  [key: string]: unknown;
}

export const ForgeAssetBrowserWrapper = (props: AssetBrowserProps) => {
  const {
    showHeader = true,
    tabs,
    viewingOwnProfile,
    initialTab,
    // TODO EN-85979: enableAssetInventoryActions,
    showManageAssets,
    pageSize = 10,
  } = props;

  const dispatch = useDispatch<ThunkDispatch<AssetBrowserState, void, AnyAction>>();
  const activeTab = useSelector(selectActiveTab);
  const initialResultsFetched = useSelector(
    (state: AssetBrowserState) => state.catalog.initialResultsFetched,
  );
  // TODO EN-85979: const results = useSelector((state: any) => state.catalog.results);
  // TODO EN-86067: const activeFilterCount = useSelector(selectActiveFilterCount);

  // TODO EN-86045:
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isFilterOpen, setIsFilterOpen] = useState(true);

  useEffect(() => {
    dispatch(setAssetBrowserProps(props));
    const tabInQueryString = getQueryParameterByName('tab');
    const defaultTab = Object.keys(tabs)[0] || '';
    dispatch(setInitialTab(tabInQueryString || initialTab || defaultTab));

    // Initialize page size in Redux state
    dispatch(updatePageSize(pageSize));

    // Fetch initial results if not already fetched
    if (!initialResultsFetched) {
      dispatch(fetchInitialResults({ pageSize }));
    }
  }, [dispatch, props, tabs, initialTab, pageSize, initialResultsFetched]);

  const handleTabChange = useCallback(
    (event: CustomEvent) => {
      const newTabIndex = event.detail;
      const tabKeys = Object.keys(tabs);
      const newTab = tabKeys[newTabIndex];
      if (newTab) {
        dispatch(changeTab(newTab));
      }
    },
    [dispatch, tabs],
  );

  // TODO EN-85314: Code below is a start for the tabs ticket.
  const renderTabs = () => {
    if (!showHeader) {
      return null;
    }

    const tabKeys = Object.keys(tabs);
    const activeTabIndex = tabKeys.indexOf(activeTab);
    // If activeTab is not found, default to 0
    const safeActiveTabIndex = activeTabIndex >= 0 ? activeTabIndex : 0;

    return (
      <div className={styles['forge-asset-browser-header']}>
        <ForgeTabBar activeTab={safeActiveTabIndex} clustered onChange={handleTabChange}>
          {tabKeys.map((tabKey) => (
            <ForgeTab key={tabKey}>{getTabDisplayText(tabKey, viewingOwnProfile)}</ForgeTab>
          ))}
        </ForgeTabBar>
      </div>
    );
  };

  if (!activeTab) {
    return null;
  }

  // TODO EN-85979:
  // const currentTab = tabs[activeTab];

  // const columns =
  //   currentTab.props?.columns?.map((columnName) => {
  //     return {
  //       header: columnName,
  //       sortable: true,
  //     };
  //   }) || [];

  // const showAssetInventoryActions = (): boolean => {
  //   return (
  //     !enableAssetInventoryActions &&
  //     activeTab !== TAB_MY_ASSETS &&
  //     activeTab !== TAB_SHARED_TO_ME
  //   );
  // };

  // View All button logic (similar to old AssetBrowser)
  const scope = 'shared.asset_browser';
  const { targetUserId, targetUserDisplayName } = _.get(window, 'socrata.assetBrowser.staticData', {});

  const allAssetsButtonTitle = viewingOwnProfile
    ? I18n.t('view_all', { scope })
    : I18n.t('view_user_assets', { scope, userName: targetUserDisplayName });
  const assetParams = viewingOwnProfile
    ? `tab=${TAB_MY_ASSETS}`
    : `ownerId=${targetUserId}&ownerName=${escape(targetUserDisplayName)}`;
  const assetUrl = `/admin/assets?${assetParams}`;

  return (
    <>
      <ForgeScaffold>
        <div slot="header" className={styles['assets-header'] + ' forge-typography--heading5'}>
          {I18n.t('header.title', { scope })}
        </div>
        <div slot="body-header" className={styles['asset-browser-scaffold-blocks']}>
          <div className={styles['asset-browser-toolbar']}>
            <div className={styles['asset-browser-toolbar-actions']}>
              {/* TODO EN-86050: <FilterChips /> */}
              {showManageAssets && (
                <ForgeButton type="button" onClick={() => (window.location.href = assetUrl)}>
                  {allAssetsButtonTitle}
                </ForgeButton>
              )}
              {/* TODO EN-86045: Forgefy filter drawer */}
              <ForgeButton variant="text" onClick={() => setIsFilterOpen((prev) => !prev)}>
                <ForgeIcon name="filter_list" slot="start"></ForgeIcon>
                {I18n.t('shared.asset_browser.filters.header.title.clear_filters_only')}
                {/* TODO EN-86067: {activeFilterCount > 0 && (
                  <ForgeBadge slot="end" theme="primary" strong>
                    {activeFilterCount}
                  </ForgeBadge>
                )} */}
              </ForgeButton>
            </div>
          </div>
          <div className={styles['asset-browser-scaffold-tabs']}>{renderTabs()}</div>
          <AssetBrowserSearch />
        </div>
        {/* TODO EN-85979: <div slot="body" className="asset-browser-scaffold-blocks">
          <AssetBrowserTable
            columns={currentTab.props?.columns || []}
            showAssetInventoryActions={showAssetInventoryActions()}
          />
        </div> */}
        {/* TODO EN-85979: <div slot="footer">{showAssetInventoryActions() && <AssetInventoryActions />}</div> */}
        <div slot="right">
          {/* TODO EN-86045: {isFilterOpen && <FiltersDrawer activeTab={activeTab} setIsFilterOpen={setIsFilterOpen} />} */}
        </div>
      </ForgeScaffold>
      <AlertWrapper />
    </>
  );
};
